/**
 * /cuenta/privacidad
 *
 * Cumplimiento Ley 1581 de 2012 (Habeas Data Colombia):
 *   - Aceptación versionada de TyC + consentimientos (GET /legal/current,
 *     /me/consents).
 *   - Exportar mis datos (GET /me/data-export → ZIP descargable).
 *   - Eliminar cuenta (POST /me/account/delete con soft-delete + grace
 *     period).
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  Download,
  FileText,
  Loader2,
  Lock,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'

import { legalApi, meApi } from '@/lib/api'
import { AccountLayout } from './layout'
import {
  Field,
  Section,
  btnSecondaryCls,
  inputCls,
} from './profile'

export function AccountPrivacyPage() {
  return (
    <AccountLayout>
      <div className="space-y-6">
        <ConsentsCard />
        <DataExportCard />
        <DeleteAccountCard />
      </div>
    </AccountLayout>
  )
}

// ─── Consents (TyC, Habeas Data, Privacidad…) ───────────────────────

function ConsentsCard() {
  const qc = useQueryClient()
  const consents = useQuery({
    queryKey: ['me', 'consents'],
    queryFn: () => legalApi.myConsents(),
    retry: false,
  })

  const accept = useMutation({
    mutationFn: (kind: string) => legalApi.acceptConsent(kind),
    onSuccess: () => {
      toast.success('Aceptado')
      qc.invalidateQueries({ queryKey: ['me', 'consents'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  const KIND_LABEL: Record<string, string> = {
    terms: 'Términos y Condiciones',
    privacy: 'Política de Privacidad',
    habeas_data: 'Autorización Habeas Data (Ley 1581/2012)',
    cookies: 'Política de Cookies',
    mandate: 'Contrato de Mandato (firma electrónica DIAN)',
    marketing: 'Comunicaciones comerciales',
  }
  const KIND_URL: Record<string, string> = {
    terms: 'https://taxor.co/legal/terms',
    privacy: 'https://taxor.co/legal/privacy',
    habeas_data: 'https://taxor.co/legal/habeas-data',
    cookies: 'https://taxor.co/legal/cookies',
    mandate: 'https://taxor.co/legal/mandate',
  }

  return (
    <Section title="Términos y consentimientos" icon={FileText}>
      {consents.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {consents.isError && (
        <p className="text-sm text-fg-muted">
          No pude cargar tus consentimientos. Contacta soporte si persiste.
        </p>
      )}
      {consents.data && consents.data.consents.length === 0 && (
        <p className="text-sm text-fg-muted">
          No tienes consentimientos registrados aún. Cuando aceptes los
          términos durante el registro o un cambio de versión, aparecerán aquí.
        </p>
      )}
      {consents.data && consents.data.consents.length > 0 && (
        <ul className="space-y-2">
          {consents.data.consents.map((c) => (
            <li
              key={c.kind + c.version}
              className="flex items-start justify-between rounded-md border border-border bg-bg-app p-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  {KIND_LABEL[c.kind] ?? c.kind}
                  {KIND_URL[c.kind] && (
                    <a
                      href={KIND_URL[c.kind]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-xs text-brand-600 hover:underline"
                    >
                      Ver términos →
                    </a>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-fg-muted">
                  Versión <code className="font-mono">{c.version}</code> ·{' '}
                  <span className="inline-flex items-center gap-1 text-success-fg">
                    <Check className="h-3 w-3" /> Aceptada{' '}
                    {c.accepted_at &&
                      new Date(c.accepted_at).toLocaleDateString('es-CO')}
                  </span>
                  {!c.is_current && (
                    <span className="ml-2 text-warning-fg">
                      (hay versión nueva)
                    </span>
                  )}
                </p>
              </div>
              {!c.is_current && (
                <button
                  type="button"
                  onClick={() => accept.mutate(c.kind)}
                  disabled={accept.isPending}
                  className="text-xs text-brand-600 hover:underline"
                >
                  {accept.isPending ? 'Aceptando…' : 'Aceptar versión nueva'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 text-xs text-fg-muted">
        Conforme a la Ley 1581 de 2012, registramos tu consentimiento por
        versión. Cuando actualizamos cualquier término, te pediremos aceptar
        la nueva versión antes de continuar.
      </p>
    </Section>
  )
}

// ─── Data export (Ley 1581 art. 8.e) ────────────────────────────────

function DataExportCard() {
  const [downloading, setDownloading] = useState(false)

  async function handleExport() {
    setDownloading(true)
    try {
      const blob = await meApi.dataExport()
      // Crear link de descarga del blob.
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `taxor-mis-datos-${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Descarga iniciada')
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Section title="Exportar mis datos" icon={Download}>
      <p className="text-sm text-fg-secondary">
        Descarga un archivo ZIP con todos los datos personales que Taxor
        guarda sobre ti: perfil, sesiones, audit log, consentimientos.
        Conforme a tu derecho de acceso (Ley 1581 art. 8.e).
      </p>
      <p className="mt-2 text-xs text-fg-muted">
        Los datos de tu empresa (facturas, clientes, productos) NO van en
        este export — son propiedad de la empresa, no tuyos individualmente.
        Si necesitas extracción de datos empresariales contacta a tu admin.
      </p>
      <button
        type="button"
        onClick={handleExport}
        disabled={downloading}
        className={`${btnSecondaryCls} mt-3`}
      >
        {downloading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Preparando ZIP…
          </>
        ) : (
          <>
            <Download className="h-4 w-4" /> Descargar mis datos
          </>
        )}
      </button>
    </Section>
  )
}

// ─── Delete account (Ley 1581 art. 8.f) ─────────────────────────────

function DeleteAccountCard() {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [reason, setReason] = useState('')

  const profile = useQuery({ queryKey: ['me', 'profile'], queryFn: () => meApi.profile() })

  const deleteAcc = useMutation({
    mutationFn: () =>
      meApi.deleteAccount({
        password,
        totp_code: profile.data?.totp_enabled ? totpCode : undefined,
        confirmation,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (data) => {
      toast.success(
        `Cuenta marcada para eliminar. Periodo de gracia: ${data.grace_period_days} días.`,
      )
      // Forzar logout — el JWT actual quedará revocado.
      setTimeout(() => {
        window.location.href = '/login'
      }, 2000)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  const canDelete =
    password.length > 0 &&
    confirmation === 'ELIMINAR MI CUENTA' &&
    (!profile.data?.totp_enabled || totpCode.length === 6) &&
    !deleteAcc.isPending

  return (
    <Section title="Eliminar mi cuenta" icon={Trash2}>
      <div className="rounded-md border border-error/30 bg-error-tint p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 flex-none text-error-fg" />
          <div>
            <p className="font-semibold text-error-fg">Acción permanente</p>
            <p className="mt-1 text-sm text-error-fg">
              Marca tu cuenta para eliminación. Tras un periodo de gracia
              (típicamente 30 días) tus datos personales se borran de forma
              permanente conforme a Ley 1581 art. 8.f.
            </p>
            <p className="mt-1 text-xs text-error-fg">
              <strong>Importante:</strong> si eres OWNER de una empresa, los
              datos de la empresa NO se eliminan — sigues siendo responsable
              de transferir el ownership antes.
            </p>
          </div>
        </div>
      </div>

      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3 text-sm text-error-fg hover:underline inline-flex items-center gap-1.5"
        >
          <Lock className="h-3.5 w-3.5" />
          Iniciar proceso de eliminación
        </button>
      )}

      {open && (
        <div className="mt-4 space-y-3 rounded-md border border-error/30 bg-bg-surface p-4">
          <Field label="Contraseña">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className={inputCls}
            />
          </Field>
          {profile.data?.totp_enabled && (
            <Field label="Código TOTP">
              <input
                type="text"
                value={totpCode}
                onChange={(e) =>
                  setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                maxLength={6}
                className={`${inputCls} font-mono w-40`}
                placeholder="123456"
              />
            </Field>
          )}
          <Field
            label="Confirmación"
            help='Escribe exactamente: ELIMINAR MI CUENTA'
          >
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className={`${inputCls} font-mono`}
              placeholder="ELIMINAR MI CUENTA"
            />
          </Field>
          <Field label="Razón (opcional)" help="Nos ayuda a mejorar.">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={2}
              className={inputCls}
              placeholder="¿Por qué te vas?"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setPassword('')
                setTotpCode('')
                setConfirmation('')
                setReason('')
              }}
              className={btnSecondaryCls}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => deleteAcc.mutate()}
              disabled={!canDelete}
              className="inline-flex items-center gap-2 rounded-md bg-error px-4 py-2 text-sm font-medium text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deleteAcc.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Eliminando…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" /> Eliminar mi cuenta
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Section>
  )
}
